import React from 'react';
import {
  SiSalesforce, SiZoho, SiHubspot,
  SiPostgresql, SiMysql,
  SiSnowflake, SiGooglecloud, SiDatabricks,
  SiGoogleanalytics, SiMixpanel,
  SiZendesk, SiIntercom, SiSlack,
  SiJira, SiGithub, SiGitlab,
  SiElasticsearch, SiSplunk, SiPrometheus, SiGoogledrive, SiGooglesheets,
} from 'react-icons/si';
import { FaDatabase, FaFile, FaFileExcel, FaCloud, FaExchangeAlt, FaTable, FaMicrosoft, FaFlask, FaAmazon } from 'react-icons/fa';

// ---------------------------------------------------------------------------
// Single source of truth: connector ID → icon component
// ---------------------------------------------------------------------------

export const connectorIconMap: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  // Files & Spreadsheets
  csv: FaFile,
  excel: FaTable,
  google_sheets: SiGooglesheets,
  excel_online: FaFileExcel,
  google_drive: SiGoogledrive,
  onedrive: FaMicrosoft,
  sharepoint: FaMicrosoft,

  // Sales & CRM
  salesforce: SiSalesforce,
  zoho: SiZoho,
  hubspot: SiHubspot,
  dynamics365: FaMicrosoft,

  // Databases
  postgresql: SiPostgresql,
  mysql: SiMysql,
  sqlserver: FaDatabase,
  oracle: FaDatabase,

  // Data Warehouses
  snowflake: SiSnowflake,
  bigquery: SiGooglecloud,
  redshift: FaAmazon,
  synapse: FaMicrosoft,
  databricks: SiDatabricks,

  // Product & Marketing Analytics
  google_analytics: SiGoogleanalytics,
  mixpanel: SiMixpanel,
  amplitude: FaExchangeAlt,

  // Support & Operations
  zendesk: SiZendesk,
  intercom: SiIntercom,
  servicenow: SiSlack,

  // Engineering Tools
  jira: SiJira,
  github: SiGithub,
  gitlab: SiGitlab,
  azure_devops: FaMicrosoft,

  // Logs & Metrics
  kusto: FaCloud,
  application_insights: FaMicrosoft,
  elasticsearch: SiElasticsearch,
  splunk: SiSplunk,
  prometheus: SiPrometheus,

  // Simulators
  simulator: FaFlask,
};

// ---------------------------------------------------------------------------
// Muted brand colors for connector icons
// ---------------------------------------------------------------------------

export const connectorColorMap: Record<string, string> = {
  // Files & Spreadsheets
  csv: '#6b7280',
  excel: '#4a8c5c',
  google_sheets: '#4a8c5c',
  excel_online: '#217346',
  google_drive: '#5a8f6b',
  onedrive: '#4a7eba',
  sharepoint: '#4a7eba',

  // Sales & CRM
  salesforce: '#5a9bd5',
  zoho: '#b55252',
  hubspot: '#d4845a',
  dynamics365: '#4a7eba',

  // Databases
  postgresql: '#5a8fbd',
  mysql: '#5a8fbd',
  sqlserver: '#6b7280',
  oracle: '#c4564a',

  // Data Warehouses
  snowflake: '#5ab8cc',
  bigquery: '#5a8fbd',
  redshift: '#c45a5a',
  synapse: '#4a7eba',
  databricks: '#d4654a',

  // Product & Marketing Analytics
  google_analytics: '#c4884a',
  mixpanel: '#7b5abf',
  amplitude: '#5a5abf',

  // Support & Operations
  zendesk: '#5a9a5a',
  intercom: '#5a8fbd',
  servicenow: '#6b7280',

  // Engineering Tools
  jira: '#5a8fbd',
  github: '#6b7280',
  gitlab: '#d4654a',
  azure_devops: '#4a7eba',

  // Logs & Metrics
  kusto: '#5a8fbd',
  application_insights: '#0078d4',
  elasticsearch: '#5abf8f',
  splunk: '#6b7280',
  prometheus: '#d4654a',

  // Simulators
  simulator: '#7b5abf',
};

// ---------------------------------------------------------------------------
// ConnectorIcon — renders an icon for a connector ID with initials fallback
// ---------------------------------------------------------------------------

export const ConnectorIcon: React.FC<{
  connectorId: string;
  name: string;
  size?: number;
}> = ({ connectorId, name, size = 20 }) => {
  const normalizedId = connectorId?.toLowerCase() ?? '';
  const IconComponent = normalizedId ? connectorIconMap[normalizedId] : null;
  const color = connectorColorMap[normalizedId] ?? '#6b7280';

  if (IconComponent) {
    return <IconComponent size={size} color={color} />;
  }

  // Fallback: initials badge
  const initials = name
    .split(' ')
    .map((w) => w.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '4px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: size * 0.45,
        fontWeight: 600,
      }}
    >
      {initials}
    </div>
  );
};